/**
 * モックマップリポジトリ実装
 * テストや開発用に利用するインメモリのリポジトリ
 */

import { v4 as uuidv4 } from 'uuid';
import { IMapRepository, MapModel } from '@/repositories/interfaces';
import { sampleMapData } from '@/data/sampleData';

/**
 * モックマップリポジトリクラス
 */
export class MockMapRepository implements IMapRepository {
  private maps: Map<string, MapModel> = new Map();

  /**
   * コンストラクタ
   * @param initialData 初期データ（省略可）
   */
  constructor(initialData?: MapModel[]) {
    // 初期データがある場合はロード
    if (initialData && initialData.length > 0) {
      initialData.forEach(map => {
        this.maps.set(map.id, {
          ...map,
          createdAt: map.createdAt || new Date(),
          updatedAt: map.updatedAt || new Date()
        });
      });
    } else {
      // サンプルデータを追加
      const sampleMap: MapModel = {
        ...sampleMapData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      this.maps.set(sampleMap.id, sampleMap);
    }
  }

  /**
   * 指定IDのマップを取得
   */
  public async getMap(id: string): Promise<MapModel | null> {
    const map = this.maps.get(id);
    return map ? { ...map } : null;
  }

  /**
   * 全マップを取得
   */
  public async getAllMaps(): Promise<MapModel[]> {
    return Array.from(this.maps.values()).map(map => ({ ...map }));
  }

  /**
   * 新しいマップを作成
   */
  public async createMap(map: Omit<MapModel, 'id' | 'createdAt' | 'updatedAt'>): Promise<MapModel> {
    const id = uuidv4();
    const now = new Date();
    const newMap: MapModel = {
      ...map,
      id,
      createdAt: now,
      updatedAt: now
    };

    this.maps.set(id, newMap);
    return { ...newMap };
  }

  /**
   * 既存マップを更新
   */
  public async updateMap(
    id: string,
    update: Partial<Omit<MapModel, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<MapModel | null> {
    const map = this.maps.get(id);
    if (!map) return null;

    const updatedMap: MapModel = {
      ...map,
      ...update,
      id,
      updatedAt: new Date()
    };

    this.maps.set(id, updatedMap);
    return { ...updatedMap };
  }

  /**
   * マップを削除
   */
  public async deleteMap(id: string): Promise<boolean> {
    if (!this.maps.has(id)) return false;
    return this.maps.delete(id);
  }

  /**
   * 全てのマップをクリア（テスト用）
   */
  public clearAll(): void {
    this.maps.clear();
  }

  /**
   * テスト用データのリセット
   */
  public resetWithSampleData(): void {
    this.clearAll();
    const sampleMap: MapModel = {
      ...sampleMapData,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    this.maps.set(sampleMap.id, sampleMap);
  }

  /**
   * テスト用データの注入
   */
  public injectData(data: MapModel | MapModel[]): void {
    if (Array.isArray(data)) {
      data.forEach(map => {
        this.maps.set(map.id, {
          ...map,
          createdAt: map.createdAt || new Date(),
          updatedAt: map.updatedAt || new Date()
        });
      });
    } else {
      this.maps.set(data.id, {
        ...data,
        createdAt: data.createdAt || new Date(),
        updatedAt: data.updatedAt || new Date()
      });
    }
  }
}